export function createFukushizaiDisplayFlushService(deps) {
    const {
        selectedFukushizaiContextRef,
        pendingFukushizaiCopy,
        selectedSetItem,
        selectedGroup,
        gridlist,
        FUKUSHIZAI_CONTEXT_DISPLAY_PANEL_TYPES,
        isEmpty,
        isEnabled,
        DicVal,
        canonicalPanelType,
        normalizeFukushizaiContext,
        readFukushizaiContextFromRow,
        normalizeFixedVerticalDisplayRows,
        restoreFukushizaiDisplayMetaFromCurrentRows,
        isFukushizaiDirtyPanel,
        clearFukushizaiDirtyPanel,
        readGridRowsPreferApi,
        applyFukushizaiDisplayRowToDetail,
        finishFukushizaiDetailSync,
        debugFukushizaiSyncLog,
        GridData,
        SetGridData
    } = deps;

    // 表示グリッド 1 枚分を detail へ戻します。
    function syncFukushizaiDisplayRowsToDetail(panelInfo, rows, options = {}) {
        const type = canonicalPanelType(DicVal(panelInfo, "type", ""));
        if (!FUKUSHIZAI_CONTEXT_DISPLAY_PANEL_TYPES.includes(type)) return false;

        const currentRows = GridData(panelInfo);
        const list = normalizeFixedVerticalDisplayRows(panelInfo, rows)
            .map((row) => restoreFukushizaiDisplayMetaFromCurrentRows(panelInfo, row, currentRows));
        if (!Array.isArray(list) || list.length === 0) return false;

        // copy preview 行は保存対象に流さないようにします。
        if (list.some((row) => isEnabled(DicVal(row, "__fukushizaiCopyPreview", false)))) return false;

        SetGridData(panelInfo, list);
        let changed = false;
        let lastResult = null;
        list.forEach((row, rowIndex) => {
            const result = applyFukushizaiDisplayRowToDetail(panelInfo, row, rowIndex, {
                context: DicVal(options, "context", null),
                timing: DicVal(options, "timing", "immediate")
            });
            if (!isEnabled(DicVal(result, "changed", false))) return;
            changed = true;
            lastResult = result;
        });
        if (!changed) return false;

        finishFukushizaiDetailSync(lastResult, pendingFukushizaiCopy, {
            refresh: false,
            flushBeforeRefresh: false,
            rerender: false,
            updateSelection: false
        });
        return true;
    }

    // 表示中の副資材グリッドをまとめて detail へ flush します。
    function flushFukushizaiDisplayGridsToDetail(options = {}) {
        const context = normalizeFukushizaiContext(DicVal(options, "context", selectedFukushizaiContextRef.current));
        if (!Array.isArray(gridlist) || gridlist.length === 0) {
            return { changed: false, updatedCount: 0, context };
        }

        const dirtyOnly = isEnabled(DicVal(options, "dirtyOnly", false));
        const stopEditing = DicVal(options, "stopEditing", true) !== false;
        debugFukushizaiSyncLog("[Kakousiji][fukushizai display flush before refresh]", {
            selectedSetItem,
            selectedGroup,
            context,
            dirtyOnly
        });

        let changed = false;
        let lastResult = null;
        let updatedCount = 0;
        const hasContextFilter = context.itemCd !== "" || context.groupNo !== "";

        gridlist.forEach((panelInfo) => {
            const type = canonicalPanelType(DicVal(panelInfo, "type", ""));
            if (!FUKUSHIZAI_CONTEXT_DISPLAY_PANEL_TYPES.includes(type)) return;
            if (dirtyOnly && !isFukushizaiDirtyPanel(panelInfo, context)) return;

            const rows = normalizeFixedVerticalDisplayRows(panelInfo, readGridRowsPreferApi(panelInfo, { stopEditing }));
            if (!Array.isArray(rows) || rows.length === 0) return;

            rows.forEach((row, rowIndex) => {
                if (hasContextFilter) {
                    const rowContext = readFukushizaiContextFromRow(row);
                    if (context.itemCd !== "" && rowContext.itemCd !== "" && rowContext.itemCd !== context.itemCd) return;
                    if (context.groupNo !== "" && rowContext.groupNo !== "" && rowContext.groupNo !== context.groupNo) return;
                }

                const result = applyFukushizaiDisplayRowToDetail(panelInfo, row, rowIndex, {
                    context,
                    timing: DicVal(options, "timing", dirtyOnly ? "group-before-switch" : "save-before-payload")
                });
                if (!isEnabled(DicVal(result, "changed", false))) return;
                changed = true;
                updatedCount += Number(DicVal(result, "updatedCount", 0)) || 0;
                lastResult = result;
            });

            clearFukushizaiDirtyPanel(panelInfo, context);
        });

        return changed
            ? { ...lastResult, changed: true, updatedCount, context: DicVal(lastResult, "context", context) }
            : { changed: false, updatedCount: 0, context };
    }

    return {
        syncFukushizaiDisplayRowsToDetail,
        flushFukushizaiDisplayGridsToDetail
    };
}
